import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { getPlants, getLinesByPlant } from '../services/metadataService';
import { useUserInfo } from '../hooks/useUserInfo';
import { isAuthorizedUser } from '../utils/authorizationUtils';

/**
 * MetadataContext - Global state management for plants and lines metadata
 * 
 * Provides shared metadata that persists across page navigation
 * - Fetches plants once on mount (only for authorized users)
 * - Fetches lines when selected plant changes
 * - Prevents duplicate API calls across components
 * - Prevents API calls for unauthorized users
 */

interface Plant {
  plant_id: number;
  plant_name: string;
  [key: string]: any;
}

interface Line {
  line_code: string;
  line_name: string;
  [key: string]: any;
}

interface MetadataContextType {
  plants: Plant[];
  selectedPlantId: number | null;
  setSelectedPlantId: (id: number | null) => void;
  lines: Line[];
  selectedLine: string | null;
  setSelectedLine: (line: string | null) => void;
  isLoading: boolean;
  error: any;
  getLineNameByCode: (lineCode: string) => string;
  getLineCodeByName: (lineName: string) => string;
  getPlantNameById: (plantId: number) => string;
}

// Create the context
const MetadataContext = createContext<MetadataContextType | undefined>(undefined);

/**
 * MetadataProvider - Wraps the application to provide global metadata state
 */
export const MetadataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [plants, setPlants] = useState<Plant[]>([]);
  const [selectedPlantId, setSelectedPlantId] = useState<number | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [selectedLine, setSelectedLine] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<any>(null);

  // Get user authorization status
  const { allRoles } = useUserInfo();
  const userIsAuthorized = isAuthorizedUser(allRoles);

  // Track if we've checked authorization at least once
  const [authChecked, setAuthChecked] = useState<boolean>(false);

  // Mark authorization as checked after first render
  useEffect(() => {
    // Only mark as checked if we have allRoles data (not null/undefined on first render)
    if (allRoles !== null && allRoles !== undefined) {
      setAuthChecked(true);
    }
  }, [allRoles]);

  // Fetch plants on mount (only once and only for authorized users)
  useEffect(() => {
    // Don't make API calls until authorization is checked
    if (!authChecked) {
      return;
    }

    // Skip API calls for unauthorized users
    if (!userIsAuthorized) {
      setIsLoading(false);
      return;
    }

    const loadPlants = async () => {
      try {
        setIsLoading(true);
        const response = await getPlants();
        if (response.success && response.data && response.data.length > 0) {
          setPlants(response.data);
          // Set first plant as default
          const defaultPlant = response.data[0];
          setSelectedPlantId(defaultPlant.plant_id);
        }
      } catch (err) {
        console.error('Failed to fetch plants:', err);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadPlants();
  }, [userIsAuthorized, authChecked]);

  // Fetch lines when selected plant changes (only for authorized users)
  useEffect(() => {
    // Don't make API calls until authorization is checked
    if (!authChecked) {
      return;
    }

    // Skip API calls for unauthorized users
    if (!userIsAuthorized || !selectedPlantId) return;

    const loadLines = async () => {
      try {
        const response = await getLinesByPlant(selectedPlantId);
        if (response.success && response.data && response.data.length > 0) {
          setLines(response.data);
          // Set first line as default if not already set
          if (!selectedLine && response.data.length > 0) {
            const defaultLine = response.data[0];
            setSelectedLine(defaultLine.line_code);
          }
        }
      } catch (err) {
        console.error('Failed to fetch lines:', err);
        setError(err);
      }
    };

    loadLines();
  }, [selectedPlantId, userIsAuthorized, authChecked]);

  // Helper function to get line name by line code
  const getLineNameByCode = useCallback((lineCode: string): string => {
    const line = lines.find(l => l.line_code === lineCode);
    return line ? line.line_name : lineCode;
  }, [lines]);

  // Helper function to get line code by line name
  const getLineCodeByName = useCallback((lineName: string): string => {
    const line = lines.find(l => l.line_name === lineName);
    return line ? line.line_code : lineName;
  }, [lines]);

  // Helper function to get plant name by plant ID
  const getPlantNameById = useCallback((plantId: number): string => {
    const plant = plants.find(p => p.plant_id === plantId);
    return plant ? plant.plant_name : '';
  }, [plants]);

  const value = useMemo(() => ({
    plants,
    selectedPlantId,
    setSelectedPlantId,
    lines,
    selectedLine,
    setSelectedLine,
    isLoading,
    error,
    getLineNameByCode,
    getLineCodeByName,
    getPlantNameById,
  }), [plants, selectedPlantId, lines, selectedLine, isLoading, error, getLineNameByCode, getLineCodeByName, getPlantNameById]);

  return (
    <MetadataContext.Provider value={value}>
      {children}
    </MetadataContext.Provider>
  );
};

/**
 * useMetadataContext - Custom hook to access metadata context
 * Must be used within MetadataProvider
 */
export const useMetadataContext = (): MetadataContextType => {
  const context = useContext(MetadataContext);

  if (!context) {
    console.warn(
      'useMetadataContext called outside MetadataProvider. Returning safe defaults.'
    );

    return {
      plants: [],
      selectedPlantId: null,
      setSelectedPlantId: () => {},
      lines: [],
      selectedLine: null,
      setSelectedLine: () => {},
      isLoading: false,
      error: null,
      getLineNameByCode: (lineCode: string) => lineCode,
      getLineCodeByName: (lineName: string) => lineName,
      getPlantNameById: () => '',
    };
  }

  return context;
};
